import React, { useRef, useCallback, useEffect, useState, useMemo } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import { makeStyles } from '@material-ui/core'
import { useInvokeDenaliAssistant, useGetAiModels } from '../../../../api/denaliAssistant'
import { useAppContext } from '../../../../redux/slices/appContext'
import Icon from '../../../../components/atoms/Icon'
import MessageInput from '../common/MessageInput'
import ClientFocusSelector from '../common/ClientFocusSelector'
import SummitConfigPopover from '../common/SummitConfigPopover'
import FlexColumn from '../../../../components/molecules/FlexColumn'
import FadeIn from '../../../../components/molecules/Transitions/FadeIn'
import Background from '../../../../providers/BackgroundProvider/Background'
import Greetings from './Greetings'
import SuggestionList from './SuggestionList'
import Notification from './ReadingListNotification'

const useStyles = makeStyles((theme) => ({
  landingContainer: {
    position: 'relative',
    maxWidth: '1000px',
    minWidth: '640px',
    height: '100%',
    margin: '0 auto',
    padding: '0 var(--padding-md)'
  },
  pulsar: {
    position: 'fixed',
    bottom: '16px',
    right: '16px',
    height: '50px',
    pointerEvents: 'none',
    userSelect: 'none'
  },
  configPopoverAnchor: {
    position: 'relative'
  },
  gearBtn: {
    width: '28px',
    height: '28px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: '1px solid var(--color-neutral-300)',
    borderRadius: 'var(--border-radius-sm)',
    backgroundColor: 'white',
    cursor: 'pointer',
    color: 'var(--color-neutral-500)',
    transition: 'all 0.15s ease',
    '&:hover': {
      backgroundColor: 'var(--color-neutral-100)',
      color: 'var(--color-neutral-700)'
    }
  },
  gearBtnOpen: {
    backgroundColor: 'var(--color-primary-050)',
    borderColor: 'var(--color-primary-300)',
    color: 'var(--color-primary-600)'
  }
}))

function LandingPage () {
  const classes = useStyles()
  const history = useHistory()
  const location = useLocation()
  const { isSummitUser } = useAppContext()
  const messageInputRef = useRef(null)
  const isMountedRef = useRef(true)
  const [hasPrefilledPrompt, setHasPrefilledPrompt] = useState(false)
  const [skillTag, setSkillTag] = useState('live')
  const [modelOverride, setModelOverride] = useState(null)
  const [temperatureOverride, setTemperatureOverride] = useState(null)
  const [maxTokensOverride, setMaxTokensOverride] = useState(null)
  const [configOpen, setConfigOpen] = useState(false)

  const { data: modelsData } = useGetAiModels({ enabled: isSummitUser })

  // Initialize client focus from URL params (if present)
  const initialFocus = useMemo(() => {
    const params = new URLSearchParams(location.search)
    const focusType = params.get('focusType')
    const focusSubjectIdentifier = params.get('focusSubjectIdentifier')
    const focusSubjectLabel = params.get('focusSubjectLabel')
    if (focusType === 'client' && focusSubjectIdentifier && focusSubjectLabel) {
      return {
        type: 'client',
        subjectIdentifier: Number(focusSubjectIdentifier) || focusSubjectIdentifier,
        subjectLabel: focusSubjectLabel
      }
    }
    return null
  }, [location.search])

  const [clientFocus, setClientFocus] = useState(initialFocus)

  // Focus mode from skill config: 'none' = hide selector, 'client' = require selection
  const focusMode = useMemo(() => {
    const params = new URLSearchParams(location.search)
    return params.get('focusMode') || null
  }, [location.search])

  // Mutations
  const invokeMutation = useInvokeDenaliAssistant()

  // Track mount status and prevent state updates after unmount
  useEffect(() => {
    isMountedRef.current = true
    return () => {
      isMountedRef.current = false
    }
  }, [])

  // Prevent React Query from updating state after unmount by using a stable mutation function
  const stableInvokeMutation = useCallback((params) => {
    if (!isMountedRef.current) {
      return Promise.resolve(null)
    }
    return invokeMutation.mutateAsync(params)
  }, [invokeMutation])

  // Get greeting based on URL params or client focus
  const getGreeting = useCallback(() => {
    const params = new URLSearchParams(location.search)

    // Check for custom greeting from skill config
    const customGreeting = params.get('greeting')
    if (customGreeting) {
      return customGreeting
    }

    // Fall back to default skill greeting
    const skillId = params.get('skillId')
    const skillName = params.get('skillName')
    if (skillId && skillName) {
      return `{{userName}}, I have put on my ${skillName} hat...`
    }

    // Check for non-client focus-based greeting from URL
    const focusType = params.get('focusType')
    const focusSubjectLabel = params.get('focusSubjectLabel')
    if (focusType && focusType !== 'client' && focusSubjectLabel) {
      return `{{userName}}, let's chat about ${focusSubjectLabel}...`
    }

    // Default to undefined (will use random greeting)
    return undefined
  }, [location.search])

  // Build context object from URL params and client focus state
  const buildContextFromParams = useMemo(() => {
    const params = new URLSearchParams(location.search)
    const context = {
      currentView: 'ai-assistant-chat',
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
    }

    // Add focus from state (client selector) or URL params (other focus types)
    if (clientFocus) {
      context.focus = clientFocus
    } else {
      // Check for non-client focus types from URL params
      const focusType = params.get('focusType')
      const focusSubjectIdentifier = params.get('focusSubjectIdentifier')
      const focusSubjectLabel = params.get('focusSubjectLabel')
      if (focusType && focusType !== 'client' && focusSubjectIdentifier && focusSubjectLabel) {
        context.focus = {
          type: focusType,
          subjectIdentifier: focusSubjectIdentifier,
          subjectLabel: focusSubjectLabel
        }
      }
    }

    // Add skillId if present
    const skillIdParam = params.get('skillId')
    if (skillIdParam) {
      context.skillId = skillIdParam
    }

    if (skillTag && skillTag !== 'live') {
      context.skillTag = skillTag
    }

    if (modelOverride) {
      context.modelOverride = modelOverride
    }
    if (temperatureOverride !== null && temperatureOverride !== undefined) {
      context.temperatureOverride = temperatureOverride
    }
    if (maxTokensOverride !== null && maxTokensOverride !== undefined) {
      context.maxTokensOverride = maxTokensOverride
    }

    return context
  }, [location.search, clientFocus, skillTag, modelOverride, temperatureOverride, maxTokensOverride])

  // Pre-fill prompt from URL query params
  useEffect(() => {
    const params = new URLSearchParams(location.search)
    const prompt = params.get('prompt')

    if (prompt && messageInputRef.current) {
      messageInputRef.current.setValue(prompt)
      setHasPrefilledPrompt(true)
    }
  }, [location.search])

  const focusRequired = focusMode === 'client' && !clientFocus

  const handleSendMessage = useCallback(async (message, attachments = []) => {
    if (!message.trim() || !isMountedRef.current) return
    if (focusMode === 'client' && !clientFocus) return

    try {
      // Send the message - thread will be created automatically if needed
      const result = await stableInvokeMutation({
        prompt: message,
        attachments,
        context: buildContextFromParams,
        options: {
          modelStrategy: 'balanced',
          streamResponse: true,
          includeTrailOfThought: true
        }
      })

      // Check if component is still mounted before proceeding
      if (!isMountedRef.current) {
        return
      }

      // If this was a new thread, update URL to the new thread
      // Use setTimeout to ensure mutation state updates complete before navigation
      if (result?.threadId) {
        setTimeout(() => {
          if (isMountedRef.current) {
            history.replace(`/denali-ai/chat/${result.threadId}`)
          }
        }, 0)
      }
    } catch (error) {
      // Only log if still mounted
      if (isMountedRef.current) {
        console.error('Failed to send message:', error)
        // TODO: Show error toast
      }
    }
  }, [stableInvokeMutation, history, buildContextFromParams, focusMode, clientFocus])

  return (
    <Background
      // light='center / cover no-repeat url(https://resources.swsapp.com/100/images/purpleMountains.webp)'
      light='var(--dai-bg)'
      dark='var(--dai-bg)'
    >
      <div className={classes.landingContainer}>
        <FlexColumn className='justify-center items-center' style={{ width: '100%', height: 'calc(100% - 15vh)', gap: '48px' }}>
          <FadeIn className='w-full'>
            <Greetings
              greeting={getGreeting()}
            />
          </FadeIn>
          <FadeIn delay={0.1} className='w-full mb-5'>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
              <div>
                {focusMode !== 'none' && (
                  <ClientFocusSelector
                    value={clientFocus}
                    onChange={setClientFocus}
                    size='xs'
                    error={focusRequired}
                  />
                )}
              </div>
              {isSummitUser && (
                <div className={classes.configPopoverAnchor}>
                  <button
                    type='button'
                    className={`${classes.gearBtn} ${configOpen ? classes.gearBtnOpen : ''}`}
                    onClick={() => setConfigOpen(prev => !prev)}
                    title='Summit settings'
                    aria-label='Summit settings'
                  >
                    <Icon name='settings' customSize='14px' />
                  </button>
                  {configOpen && (
                    <SummitConfigPopover
                      skillTag={skillTag}
                      onSkillTagChange={setSkillTag}
                      models={modelsData?.models}
                      modelOverride={modelOverride}
                      onModelOverrideChange={setModelOverride}
                      temperatureOverride={temperatureOverride}
                      onTemperatureOverrideChange={setTemperatureOverride}
                      maxTokensOverride={maxTokensOverride}
                      onMaxTokensOverrideChange={setMaxTokensOverride}
                      onClose={() => setConfigOpen(false)}
                    />
                  )}
                </div>
              )}
            </div>
            <MessageInput
              ref={messageInputRef}
              onSend={handleSendMessage}
              disabled={focusRequired}
              placeholder='What do you want to know?'
              minRows={4}
            />
          </FadeIn>
          {!hasPrefilledPrompt && (
            <FadeIn delay={1} duration={1}>
              <SuggestionList
                onSuggestionClick={(prompt) => {
                // Set the suggestion in the input so user can review/modify before sending
                  messageInputRef.current?.setValue(prompt)
                }}
              />
            </FadeIn>
          )}
        </FlexColumn>
        <Notification />
        <FadeIn className={classes.pulsar} delay={0.5} duration={1}>
          <img src='/img/orion_pulsar.svg' alt='Orion' className={classes.pulsar} />
        </FadeIn>
      </div>
    </Background>
  )
}

export default LandingPage
